import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import BookingFlow from "@/components/magic-flow/BookingFlow";
import { FlowLinkInBio } from "@/components/booking-flow/FlowLinkInBio";
import { ScreenStack } from "@/components/booking-flow/FlowScaffold";
import { useBookingHashView } from "@/components/booking-flow/useBookingHashView";
import { LinkInBioAPI } from "@/lib/linkinbio-api";
import { getBookingTheme, flowThemeVars, FLOW_DEFAULTS } from "@/components/booking-flow/themes";
import NotFound from "@/pages/NotFound";
import { ProviderPageSkeleton } from "@/components/ui/skeleton-block";

// ── Types ────────────────────────────────────────────────────────────────────

interface BusinessData {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  ownerFirstName?: string | null;
  website?: string;
  instagram?: string;
  timezone: string;
  welcomeMessage?: string;
  welcomeGif?: string;
  stripeAccountId?: string;
  stripeChargesEnabled?: boolean;
  bookingFlowThemeKey?: string;
  platformFeePercentage: number;
  enableTips: boolean;
  suggestedTipPercentages: number[];
  userId?: string;
  title?: string | null;
  bio?: string | null;
  logoUrl?: string | null;
  cancellationFeePolicy?: string | null;
}

interface BusinessService {
  id: string;
  name: string;
  description: string;
  duration: number;
  price: number;
  bufferTime: number;
}

interface BusinessHours {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  enabled: boolean;
}

interface LinkInBioData {
  business: {
    id: string;
    name: string;
    business_logo_url: string | null;
    instagram_url: string | null;
    x_url: string | null;
    threads_url: string | null;
    tiktok_url: string | null;
  };
  custom_links: Array<{
    id: string;
    title: string;
    url: string;
    sort_order: number;
  }>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PublicBusinessPage() {
  const { bookingUrl } = useParams<{ bookingUrl: string }>();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [business, setBusiness] = useState<BusinessData | null>(null);
  const [services, setServices] = useState<BusinessService[]>([]);
  const [businessHours, setBusinessHours] = useState<BusinessHours[]>([]);
  const [linkInBioData, setLinkInBioData] = useState<LinkInBioData | null>(null);
  const { view: publicView, openBooking, exitBooking } = useBookingHashView(() => `/${bookingUrl}`);

  useEffect(() => {
    if (!bookingUrl) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    async function load() {
      try {
        // Primary lookup — get-booking-business has no enablement gate
        const { data: response, error: fnError } = await supabase.functions.invoke(
          "get-booking-business",
          { body: { bookingUrl } }
        );

        if (fnError) {
          // FunctionsFetchError = network/CORS failure — not a 404
          // FunctionsHttpError = function returned non-2xx (business not found)
          if (fnError.name === 'FunctionsFetchError') {
            setFetchError(true);
          } else {
            setNotFound(true);
          }
          setLoading(false);
          return;
        }

        if (!response?.business) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const bd = response.business;

        const transformedBusiness: BusinessData = {
          id: bd.id,
          name: bd.name,
          email: bd.email,
          phone: bd.phone,
          ownerFirstName: null,
          website: bd.website,
          instagram: bd.instagram,
          timezone: bd.timezone,
          welcomeMessage: bd.welcomeMessage,
          welcomeGif: bd.welcomeGif,
          stripeAccountId: bd.stripeAccountId,
          stripeChargesEnabled: bd.stripeChargesEnabled ?? false,
          bookingFlowThemeKey: bd.bookingFlowThemeKey || 'figgy_purple',
          platformFeePercentage: 0.5,
          enableTips: bd.enableTips,
          suggestedTipPercentages: bd.suggestedTipPercentages,
          userId: bd.userId,
          title: bd.title || null,
          bio: bd.bio || null,
          // Prefer onboarding logo; link-in-bio logo added below if available
          logoUrl: bd.logoUrl || null,
          cancellationFeePolicy: bd.cancellationFeePolicy || null,
        };

        setBusiness(transformedBusiness);
        document.title = `${bd.name} | Figgy`;

        // Parallel: services, hours, link-in-bio, owner name (all optional)
        const [servicesResult, hoursResult, libResult, ownerResult] = await Promise.allSettled([
          supabase
            .from("business_services")
            .select("*")
            .eq("business_id", bd.id)
            .order("name"),
          supabase
            .from("business_hours")
            .select("*")
            .eq("business_id", bd.id)
            .order("day_of_week"),
          supabase.rpc("get_link_in_bio_data", { p_slug: bookingUrl }),
          bd.userId
            ? supabase.from("profiles").select("first_name").eq("id", bd.userId).maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        ]);

        if (ownerResult.status === "fulfilled" && ownerResult.value.data?.first_name) {
          const firstName = ownerResult.value.data.first_name as string;
          setBusiness(prev => (prev ? { ...prev, ownerFirstName: firstName } : prev));
        }

        if (servicesResult.status === "fulfilled" && !servicesResult.value.error) {
          setServices(servicesResult.value.data ?? []);
        }

        if (hoursResult.status === "fulfilled" && !hoursResult.value.error) {
          setBusinessHours(hoursResult.value.data ?? []);
        }

        if (libResult.status === "fulfilled" && !libResult.value.error && libResult.value.data) {
          setLinkInBioData(libResult.value.data as LinkInBioData);
          // Use link-in-bio logo as fallback if onboarding logo absent
          setBusiness(prev =>
            prev && !prev.logoUrl
              ? { ...prev, logoUrl: libResult.value.data.business.business_logo_url ?? null }
              : prev
          );
        }
      } catch (err) {
        console.error("PublicBusinessPage load error:", err);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    }

    load();

    return () => {
      document.title = "Figgy - Effortless Client Bookings for Solo Professionals";
    };
  }, [bookingUrl]);


  // ── Render states ──────────────────────────────────────────────────────────

  if (loading) {
    return <ProviderPageSkeleton />;
  }

  if (fetchError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md px-4">
          <p className="text-muted-foreground">Couldn't load this page. Please try again.</p>
        </div>
      </div>
    );
  }

  if (notFound || !business) {
    return <NotFound />;
  }

  const hasServices = services.length > 0;
  const hasHours = businessHours.some(h => h.enabled);
  const canBook = hasServices && hasHours;

  const socialLinks: Array<{ platform: string; url: string }> = linkInBioData
    ? [
        { platform: "instagram", url: linkInBioData.business.instagram_url ?? "" },
        { platform: "x", url: linkInBioData.business.x_url ?? "" },
        { platform: "threads", url: linkInBioData.business.threads_url ?? "" },
        { platform: "tiktok", url: linkInBioData.business.tiktok_url ?? "" },
      ]
    : [];

  // ── Case 1: Link-in-bio enabled and booking is available ──────────────────
  // Show link-in-bio first, then transition into booking as a separate screen.
  // Uses the unified booking theme so LinkInBio and booking flow are visually consistent.
  const bookingTheme = getBookingTheme(business?.bookingFlowThemeKey || 'figgy_purple');

  if (linkInBioData && canBook) {
    // Link-in-bio and booking flow live in one screen stack so "Book
    // appointment" pushes into the flow (and back pops out), mirroring
    // the design's iOS-style navigation.
    return (
      <div
        style={{
          position: "relative",
          height: "100dvh",
          overflow: "hidden",
          background: bookingTheme.flowBg || FLOW_DEFAULTS.bg,
          ...flowThemeVars(bookingTheme),
        } as React.CSSProperties}
      >
        <ScreenStack
          screenKey={publicView}
          order={publicView === "links" ? 0 : 1}
          noSnapshotKeys={["booking"]}
        >
          {publicView === "links" ? (
            <FlowLinkInBio
              theme={bookingTheme}
              businessName={business.name}
              displayName={business.ownerFirstName || business.name}
              handle={business.instagram ? `@${business.instagram}` : undefined}
              bio={business.bio || business.title || undefined}
              logoUrl={linkInBioData.business.business_logo_url ?? business.logoUrl}
              socialLinks={socialLinks.filter(s => s.url).map(s => ({ platform: s.platform, url: s.url }))}
              customLinks={linkInBioData.custom_links.map(l => ({
                id: l.id,
                title: l.title,
                url: l.url,
              }))}
              onBook={openBooking}
              onLinkClick={(linkId) => { LinkInBioAPI.trackLinkClick(linkId).catch(() => {}); }}
            />
          ) : (
            <div id="booking" style={{ position: "absolute", inset: 0 }}>
              <BookingFlow
                businessData={business}
                services={services}
                businessHours={businessHours}
                onExit={exitBooking}
              />
            </div>
          )}
        </ScreenStack>
      </div>
    );
  }

  // ── Case 2: Link-in-bio enabled, no booking available ─────────────────────
  // Same design-system link-in-bio, with a notice in place of the booking CTA.
  if (linkInBioData && !canBook) {
    const contact = business.phone || business.email;
    const notice = contact
      ? `Not accepting online bookings yet — contact ${business.phone && business.email ? `${business.phone} or ${business.email}` : contact} to book.`
      : 'Not accepting online bookings yet. Check back soon.';
    return (
      <div
        style={{
          position: "relative",
          height: "100dvh",
          overflow: "hidden",
          background: bookingTheme.flowBg || FLOW_DEFAULTS.bg,
          ...flowThemeVars(bookingTheme),
        } as React.CSSProperties}
      >
        <FlowLinkInBio
          theme={bookingTheme}
          businessName={business.name}
          displayName={business.ownerFirstName || business.name}
          handle={business.instagram ? `@${business.instagram}` : undefined}
          bio={business.bio || business.title || undefined}
          logoUrl={linkInBioData.business.business_logo_url ?? business.logoUrl}
          socialLinks={socialLinks.filter(s => s.url).map(s => ({ platform: s.platform, url: s.url }))}
          customLinks={linkInBioData.custom_links.map(l => ({ id: l.id, title: l.title, url: l.url }))}
          onBook={() => {}}
          showBookButton={false}
          notice={notice}
          onLinkClick={(linkId) => { LinkInBioAPI.trackLinkClick(linkId).catch(() => {}); }}
        />
      </div>
    );
  }

  // ── Case 3: Booking available, no link-in-bio ─────────────────────────────
  if (canBook) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="flex-1" id="booking">
          <BookingFlow
            businessData={business}
            services={services}
            businessHours={businessHours}
          />
        </div>
        <PageFooter />
      </div>
    );
  }

  // ── Case 4: Nothing available — basic profile page ────────────────────────
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md px-4">
          {business.logoUrl && (
            <img
              src={business.logoUrl}
              alt={business.name}
              className="w-20 h-20 rounded-2xl object-cover mx-auto shadow-sm"
            />
          )}
          <h1 className="text-2xl font-bold">{business.name}</h1>
          {business.title && (
            <p className="text-sm font-medium text-foreground/70">{business.title}</p>
          )}
          {business.bio && (
            <p className="text-muted-foreground">{business.bio}</p>
          )}
          <NotAcceptingYet business={business} />
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function NotAcceptingYet({ business }: { business: BusinessData }) {
  return (
    <div className="text-center space-y-2 py-8 px-4 max-w-md mx-auto">
      <p className="text-muted-foreground">
        This business isn&apos;t accepting online bookings yet.
      </p>
      {(business.phone || business.email) && (
        <p className="text-sm text-muted-foreground">
          Contact them directly
          {business.phone && ` at ${business.phone}`}
          {business.phone && business.email && " or"}
          {business.email && ` at ${business.email}`}.
        </p>
      )}
      {!business.phone && !business.email && (business.website || business.instagram) && (
        <div className="flex gap-4 justify-center pt-2">
          {business.website && (
            <a
              href={business.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline"
            >
              Visit Website
            </a>
          )}
          {business.instagram && (
            <a
              href={
                business.instagram.startsWith("http")
                  ? business.instagram
                  : `https://instagram.com/${business.instagram}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline"
            >
              Instagram
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function PageFooter() {
  return (
    <footer className="py-4 text-center text-xs text-muted-foreground border-t">
      By continuing you agree to our{" "}
      <Link to="/legal/terms-and-conditions" className="underline hover:text-foreground">
        Terms of Service
      </Link>{" "}
      and{" "}
      <Link to="/legal/privacy-policy" className="underline hover:text-foreground">
        Privacy Policy
      </Link>
    </footer>
  );
}
